/**
 * Utilities for preserving query parameters through OAuth redirect flows.
 * When users click Google login with query params like ?redirect=tenant-strid,
 * those params are lost when Google redirects back. These utilities save
 * them to sessionStorage before redirect and restore them after.
 */

const OAUTH_PARAMS_KEY = 'auth:oauthQueryParams'
/**
 * Relevant query parameters to preserve through OAuth flow.
 * These are params that affect the authentication/redirect behavior.
 */
const RELEVANT_PARAMS = ['redirect', 'action'] as const

type RelevantParam = (typeof RELEVANT_PARAMS)[number]

/**
 * Extracts and stores relevant query parameters to sessionStorage
 * before initiating OAuth redirect.
 *
 * Should be called before signInWithRedirect().
 */
export function saveOAuthQueryParams(): void {
  try {
    const url = new URL(window.location.href)
    const paramsToSave: Record<string, string> = {}

    for (const param of RELEVANT_PARAMS) {
      const value = url.searchParams.get(param)
      if (value) {
        paramsToSave[param] = value
      }
    }

    if (Object.keys(paramsToSave).length > 0) {
      sessionStorage.setItem(OAUTH_PARAMS_KEY, JSON.stringify(paramsToSave))
      console.log('[OAUTH_PARAMS] Saved query params', paramsToSave)
    }
  } catch (error) {
    console.warn('[OAUTH_PARAMS] Failed to save query params', error)
  }
}

/**
 * Restores previously saved query parameters to the current URL.
 * Merges stored params with existing URL params (stored params take precedence).
 *
 * Should be called after OAuth callback, before reading query params.
 */
export function restoreOAuthQueryParams(): void {
  try {
    const stored = sessionStorage.getItem(OAUTH_PARAMS_KEY)
    if (!stored) {
      return
    }

    const paramsToRestore = JSON.parse(stored) as Record<string, string>
    if (Object.keys(paramsToRestore).length === 0) {
      return
    }

    const url = new URL(window.location.href)

    // Restore stored params (they take precedence over existing params)
    for (const [key, value] of Object.entries(paramsToRestore)) {
      if (RELEVANT_PARAMS.includes(key as RelevantParam)) {
        url.searchParams.set(key, value)
        //
      }
    }

    // Update URL without reload
    const newUrl =
      url.pathname + (url.search ? url.search : '') + (url.hash || '')
    window.history.replaceState({}, '', newUrl)

    console.log('[OAUTH_PARAMS] Restored query params', paramsToRestore)
  } catch (error) {
    console.warn('[OAUTH_PARAMS] Failed to restore query params', error)
  }
}

/**
 * Clears stored OAuth query parameters from sessionStorage.
 * Should be called after successfully processing redirect or completing validation.
 */
export function clearOAuthQueryParams(): void {
  try {
    sessionStorage.removeItem(OAUTH_PARAMS_KEY)
    console.log('[OAUTH_PARAMS] Cleared stored query params')
  } catch (error) {
    console.warn('[OAUTH_PARAMS] Failed to clear query params', error)
  }
}
